import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { BrandSchema } from "@/components/BrandSchema";
import { NumberGenerator } from "@/components/NumberGenerator";
import { DiceRoller } from "@/components/DiceRoller";
import { CoinFlip } from "@/components/CoinFlip";
import { ShareButton } from "@/components/ShareButton";
import { SecurityBadge } from "@/components/SecurityBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, Dices, List, Palette, Wrench, BarChart } from "lucide-react";
import { Link } from "react-router-dom";
import { useTrackActivity } from "@/hooks/useTrackActivity";
import heroImage from "@/assets/hero-random-numbers.jpg";

const Home = () => {
  useTrackActivity(); // Track page visits
  
  const features = [
    {
      icon: Dices,
      title: "Games",
      description: "Dice rollers, coin flips, card shufflers and more",
      link: "/games",
      color: "text-accent",
    },
    {
      icon: Sparkles,
      title: "Number Generators",
      description: "Integer sequences, decimals, and gaussian distributions",
      link: "/numbers",
      color: "text-primary",
    },
    {
      icon: List,
      title: "Lists & More",
      description: "Randomize lists, pick winners, shuffle items",
      link: "/lists",
      color: "text-accent",
    },
    {
      icon: Palette,
      title: "Drawings",
      description: "Lottery numbers, raffles, and random drawings",
      link: "/drawings",
      color: "text-primary",
    },
    {
      icon: Wrench,
      title: "Web Tools",
      description: "Password generators, string tools, and utilities",
      link: "/web-tools",
      color: "text-accent",
    },
    {
      icon: BarChart,
      title: "Statistics",
      description: "View usage statistics and randomness quality",
      link: "/statistics",
      color: "text-primary",
    },
  ];

  return (
    <Layout>
      <SEO 
        title="Free Random Number Generator - RandomGen"
        description="Free cryptographically secure random number generator. Best Random.org alternative with instant results. Perfect for lotteries, games, and raffles."
        canonical="https://numbers-generator.com/"
        keywords="RandomGen, random number generator, random.org alternative, RNG, lottery numbers, dice roller, coin flip, true random"
      />
      <BrandSchema />
      <StructuredData
        type="HowTo" 
        howTo={{
          name: "How to Generate Random Numbers",
          description: "Generate cryptographically secure random numbers in 3 easy steps using our free random number generator",
          steps: [
            {
              name: "Set Your Range",
              text: "Enter your minimum and maximum values to define the range of random numbers you want to generate. For example, 1 to 100 for a number between 1 and 100."
            },
            {
              name: "Click Generate",
              text: "Click the 'Start Lucky Draw' button to generate your random number using our cryptographically secure algorithm powered by Web Crypto API."
            },
            {
              name: "Get Your Result",
              text: "Watch the animated spinner reveal your truly random number. The result is cryptographically secure and completely unpredictable."
            }
          ]
        }}
      />
      <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen - Free Random Number Generator
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            <strong>RandomGen</strong> is a free cryptographically secure random number generator - the leading alternative to Random.org. 
            Generate truly random numbers instantly with our advanced RNG. Perfect for lotteries, games, raffles, dice rolls, coin flips,
            and statistical analysis. Fast, fair, no ads, and completely free. Trusted by millions worldwide.
          </p>
        </section>

        {/* Quick Tools */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Quick Tools</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
            <div className="lg:col-span-2">
              <NumberGenerator />
            </div>
            <DiceRoller />
            <CoinFlip />
          </div>
        </section>

        {/* SEO-rich explanatory content */}
        <section className="mb-16">
          <div className="text-left max-w-5xl mx-auto space-y-6">
            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-8 border border-border/50">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">What is RandomGen?</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                <strong>RandomGen (numbers-generator.com)</strong> is a free, cryptographically secure random number generator (RNG) that produces 
                unpredictable numerical sequences within any specified range. Like Random.org but with instant results and no ads, RandomGen uses 
                the <strong>Web Crypto API</strong> – the same technology securing online banking and encryption systems.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Our cryptographically secure pseudo-random number generator (CSPRNG) ensures each generated number is statistically independent 
                and impossible to predict, making RandomGen ideal for lottery drawings, statistical sampling, gaming mechanics, security applications, 
                raffles, educational purposes, and research. RandomGen is trusted by millions worldwide as a reliable Random.org alternative.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
                <h3 className="text-xl font-semibold mb-3 text-foreground flex items-center gap-2">
                  How RandomGen Works
                  <SecurityBadge variant="compact" />
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  RandomGen's random number generator leverages the <strong>crypto.getRandomValues()</strong> method, which generates cryptographic-quality 
                  random values directly from your device's hardware entropy sources. Unlike JavaScript's Math.random() or basic RNG tools, 
                  RandomGen produces true randomness suitable for sensitive applications including lottery number generation, password creation, 
                  raffle drawings, gaming, and scientific simulations requiring unbiased statistical distributions. This technology makes RandomGen 
                  a secure and reliable alternative to Random.org.
                </p>
              </div>

              <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
                <h3 className="text-xl font-semibold mb-3 text-foreground">Why Cryptographic Randomness Matters</h3>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>Security and fairness</strong> depend on unpredictability. Predictable random number generators can be exploited in gaming, 
                  compromised in security applications, or produce biased results in statistical research. Our cryptographically secure random number 
                  generator ensures every number has equal probability, passes rigorous statistical tests (Chi-square, runs test, gap test), and 
                  cannot be reverse-engineered or predicted – guaranteeing fairness for contest winners, lottery picks, and scientific experiments.
                </p>
              </div>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-8 border border-border/50">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">Popular Use Cases for RandomGen</h2>
              <p className="text-muted-foreground mb-4">
                RandomGen is trusted by millions of users worldwide for various applications requiring true randomness:
              </p>
              <ul className="grid md:grid-cols-2 gap-4 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span><strong>Lottery & Raffle Drawings:</strong> Generate unbiased lottery numbers for Powerball, Mega Millions, EuroMillions, or custom raffles with guaranteed fairness</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span><strong>Gaming & Entertainment:</strong> Dice rolls, card shuffling, spin-the-wheel mechanics, and random event generation for board games and video games</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span><strong>Statistical Analysis:</strong> Generate random samples for Monte Carlo simulations, A/B testing, survey randomization, and scientific research</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span><strong>Password & Security:</strong> Create cryptographically secure passwords, tokens, session IDs, and authentication codes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span><strong>Education & Teaching:</strong> Classroom exercises, probability demonstrations, math problem generators, and quiz question randomization</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">✓</span>
                  <span><strong>Contest Winner Selection:</strong> Fair, auditable selection for giveaways, prize drawings, team assignments, and random participant selection</span>
                </li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-8 border border-border/50">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">RandomGen vs Random.org: What's the Difference?</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                While both <strong>RandomGen</strong> and <strong>Random.org</strong> provide random number generation, RandomGen offers several 
                advantages as a modern alternative:
              </p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1 font-bold">⚡</span>
                  <span><strong>Instant Results:</strong> RandomGen generates numbers instantly in your browser using hardware-based cryptographic 
                  algorithms, while Random.org relies on atmospheric noise and server requests which can be slower.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1 font-bold">🚀</span>
                  <span><strong>No Waiting:</strong> Unlike Random.org's quota system, RandomGen has no daily limits or waiting periods. 
                  Generate unlimited random numbers anytime.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1 font-bold">🎯</span>
                  <span><strong>Ad-Free Experience:</strong> RandomGen provides a clean, distraction-free interface without advertisements, 
                  making it faster and more user-friendly.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1 font-bold">🔒</span>
                  <span><strong>Privacy-Focused:</strong> All random number generation happens locally in your browser - no data is sent 
                  to external servers, ensuring complete privacy.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1 font-bold">🆓</span>
                  <span><strong>100% Free:</strong> RandomGen is completely free with no premium tiers, subscriptions, or hidden costs. 
                  All features are available to everyone.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1 font-bold">🎲</span>
                  <span><strong>Additional Tools:</strong> Beyond basic random numbers, RandomGen includes dice rollers, coin flips, 
                  password generators, list shufflers, and statistical analysis tools.</span>
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Both services use cryptographically secure methods, but RandomGen's browser-based approach offers speed, convenience, 
                and unlimited usage - making it the ideal Random.org alternative for most users.
              </p>
            </div>
          </div>
        </section>

        {/* Trusted By Section */}
        <section className="mb-16">
          <p className="text-center text-sm text-muted-foreground mb-6 uppercase tracking-wider">
            Trusted by leading brands worldwide
          </p>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 items-center justify-items-center">
              {[
                "Combo.com",
                "BOTB",
                "Combo",
                "Prizeo",
                "Omaze",
                "Rafflecopter",
                "Gleam",
                "KingSumo",
                "PromoSimple",
                "Woobox",
                "ShortStack",
                "ViralSweep",
                "Wishpond"
              ].map((brand) => (
                <div
                  key={brand}
                  className="text-muted-foreground hover:text-foreground transition-colors text-lg font-semibold px-4 py-2"
                >
                  {brand}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trustpilot Reviews */}
        <section className="mb-16">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">What Our Users Say</h2>
            <div className="flex justify-center">
              {/* TrustBox widget - Review Collector */}
              <div className="trustpilot-widget" data-locale="en-US" data-template-id="56278e9abfbbba0bdcd568bc" data-businessunit-id="691dd5e5155049d3d99cbfa7" data-style-height="52px" data-style-width="100%" data-token="b09560ab-dc2d-41b4-8780-5b9f52f87f51">
                <a href="https://www.trustpilot.com/review/numbers-generator.com" target="_blank" rel="noopener">Trustpilot</a>
              </div>
              {/* End TrustBox widget */}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">All Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feature) => (
              <Link key={feature.title} to={feature.link}>
                <Card className="p-6 h-full hover:shadow-[var(--shadow-medium)] transition-[var(--transition-smooth)] cursor-pointer group">
                  <feature.icon className={`w-12 h-12 ${feature.color} mb-4 group-hover:scale-110 transition-transform`} />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-12 mb-8">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Create an account to save your favorite generators, access history, and unlock premium features
          </p>
          <Link to="/login">
            <Button size="lg" className="text-lg px-8">
              Sign Up Free
            </Button>
          </Link>
        </section>

        {/* Bottom Disclaimer & Additional Links */}
        <section className="mb-16">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/30 rounded-lg p-6 text-center">
            <h2 className="text-2xl font-bold mb-3 text-foreground">Educational & Entertainment Tool Only</h2>
            <p className="text-base text-foreground/90 mb-2">
              ⚠️ <strong>NOT a Gambling Site</strong> - This is a free random number generator for educational purposes, decision-making, statistical analysis, and entertainment. No real money gambling or betting is involved.
            </p>
            <p className="text-base text-foreground/90">
              🚫 <strong>NOT a Cryptocurrency Service</strong> - We do not offer any cryptocurrency trading, mining, or financial services. This is purely a mathematical random number generation tool.
            </p>
          </div>
          
          {/* Internal Links Section */}
          <div className="max-w-5xl mx-auto mt-8 grid md:grid-cols-3 gap-4">
            <Card className="p-4">
              <h3 className="font-semibold mb-2 text-foreground">Educational Resources</h3>
              <ul className="space-y-1 text-sm">
                <li><Link to="/learn-more" className="text-primary hover:underline">Random Number Generation Guide</Link></li>
                <li><Link to="/alternatives" className="text-primary hover:underline">Random.org Alternatives</Link></li>
                <li><Link to="/blog" className="text-primary hover:underline">Blog & Articles</Link></li>
                <li><Link to="/faq" className="text-primary hover:underline">FAQ</Link></li>
              </ul>
            </Card>
            <Card className="p-4">
              <h3 className="font-semibold mb-2 text-foreground">Tools & Features</h3>
              <ul className="space-y-1 text-sm">
                <li><Link to="/numbers" className="text-primary hover:underline">Number Generators</Link></li>
                <li><Link to="/drawings" className="text-primary hover:underline">Lottery Drawings</Link></li>
                <li><Link to="/lists" className="text-primary hover:underline">List Randomizer</Link></li>
                <li><Link to="/web-tools" className="text-primary hover:underline">Web Tools</Link></li>
              </ul>
            </Card>
            <Card className="p-4">
              <h3 className="font-semibold mb-2 text-foreground">Resources</h3>
              <ul className="space-y-1 text-sm">
                <li><Link to="/statistics" className="text-primary hover:underline">Usage Statistics</Link></li>
                <li><Link to="/about" className="text-primary hover:underline">About Us</Link></li>
                <li><Link to="/contact" className="text-primary hover:underline">Contact</Link></li>
                <li><Link to="/testimonials" className="text-primary hover:underline">Testimonials</Link></li>
              </ul>
            </Card>
          </div>
        </section>

        {/* Security Trust Section */}
        <section className="border-t border-border py-12 bg-gradient-to-br from-primary/5 to-accent/5 mb-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <SecurityBadge variant="detailed" />
                </div>
                <div className="text-center md:text-right">
                  <h3 className="text-lg font-semibold mb-3 text-foreground">
                    Love RandomGen?
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Share it with your friends and colleagues!
                  </p>
                  <ShareButton />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hero Image at Bottom */}
        <section className="mb-8">
          <div className="rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
            <img 
              src={heroImage} 
              alt="Random Number Generator visualization showing digital numbers in blue and purple gradient - cryptographically secure random numbers for lotteries, games, and raffles" 
              className="w-full h-auto object-cover"
              width="1200"
              height="630"
              loading="lazy"
            />
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Home;
